import { useEffect, useState } from 'react'
import type { CardArtAssetName } from '../../types'

interface MLVisaCardImageProps {
  cardArt?: CardArtAssetName | null
  hideShadow?: boolean
  className?: string
  fill?: boolean
  width?: number
  height?: number
}

const MLVisaCardImage = (props: MLVisaCardImageProps) => {
  const { cardArt = 'greenlineV0', hideShadow = false, className, fill, width, height } = props

  // handle possible null value
  const cardArtId = cardArt ?? 'greenlineV0'
  const src = `https://cdn.missionlane.com/images/card-art/${cardArtId}.svg`

  const [svgContent, setSvgContent] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch SVG: ${res.status}`)
        const contentType = res.headers.get('content-type') ?? ''
        if (!contentType.includes('image/svg+xml')) {
          throw new Error(`Unexpected content-type: ${contentType}`)
        }
        return res.text()
      })
      .then((text) => {
        if (!cancelled) {
          // Strip any script tags for safety
          const sanitized = text.replace(/<script[\s\S]*?<\/script>/gi, '')
          setSvgContent(sanitized)
        }
      })
      .catch(() => {
        // Silently fail — background-image fallback remains visible
      })

    return () => {
      cancelled = true
    }
  }, [src])

  // Render inline SVG at full device-pixel resolution. Safari rasterizes
  // externally-referenced SVGs at their intrinsic dimensions,
  // causing pixelation on Retina displays. Inline SVGs render as DOM elements
  // at the container's actual resolution.
  const sizeClasses = fill ? 'absolute inset-0' : ''
  const sizeStyles = !fill && width && height ? { width, height } : {}
  const shadowClasses = hideShadow ? '' : 'rounded-[3%/5%] shadow-card overflow-hidden'

  return (
    <div
      data-testid="ml-visa-card"
      role="img"
      aria-label="Mission Lane Visa card"
      className={`${sizeClasses} ${shadowClasses} ${!svgContent ? 'bg-contain bg-center bg-no-repeat' : ''} ${className ?? ''}`.trim()}
      style={{
        ...sizeStyles,
        // Background-image fallback while SVG is being fetched
        ...(svgContent ? {} : { backgroundImage: `url(${src})` }),
      }}
    >
      {svgContent && (
        <div
          className="w-full h-full [&>svg]:w-full [&>svg]:h-full"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      )}
    </div>
  )
}

export default MLVisaCardImage
